import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaFacebook, FaInstagram, FaLinkedin, FaTwitter, FaYoutube, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { API_BASE } from '../../config/api';
import { DEFAULT_JEWELRY_METALS } from '../../utils/materialCatalog';
import { formatMaterialDisplayLabel, normalizeMaterialLabel } from '../../utils/materialLabels';
import { bindStorefrontRefresh } from '../../utils/storefrontRefresh';
import './Footer.css';

const DEFAULT_FOOTER_CONTENT = {
  description:
    'Discover exquisite jewelry crafted with precision and passion. From traditional designs to modern elegance, we bring you the finest collection of rings, necklaces, earrings, and more.',
  phone: '+91 96965 81515',
  email: 'support.panchalijewels@gmail.com',
  address: 'Mumbai, Maharashtra, India',
  quickLinks: [
    { label: 'PRODUCTS', url: '/store' },
    { label: 'ABOUT US', url: '/about' },
    { label: 'BESPOKE JEWELRY', url: '/bespoke-jewelry' },
    { label: 'CONTACT US', url: '/contact' },
    { label: 'TERMS & CONDITIONS', url: '/terms' },
  ],
  customerServiceLinks: [
    { label: 'My Account', url: '/account' },
    { label: 'Track Order', url: '/order' },
    { label: 'Shopping Cart', url: '/cart' },
    { label: 'Wishlist', url: '/wishlist' },
    { label: 'Help & Support', url: '/contact' },
  ],
  socialLinks: [
    { label: 'Facebook', url: 'https://facebook.com/panchalijewels' },
    { label: 'Instagram', url: 'https://instagram.com/panchalijewels' },
    { label: 'LinkedIn', url: 'https://linkedin.com/company/panchalijewels' },
    { label: 'Twitter', url: 'https://twitter.com/panchalijewels' },
    { label: 'YouTube', url: 'https://youtube.com/panchalijewels' },
  ],
};

const SOCIAL_ICONS = {
  facebook: FaFacebook,
  instagram: FaInstagram,
  linkedin: FaLinkedin,
  twitter: FaTwitter,
  youtube: FaYoutube,
};

const normalizeCategoryList = (categories = []) =>
  [...new Set(
    (Array.isArray(categories) ? categories : [])
      .map((category) => normalizeMaterialLabel(category))
      .filter(Boolean)
  )];

const normalizeLinks = (links = []) =>
  (Array.isArray(links) ? links : [])
    .filter((link) => link && link.isHidden !== true)
    .sort((left, right) => Number(left.order || 0) - Number(right.order || 0));

const Footer = () => {
  const navigate = useNavigate();
  const [productCategories, setProductCategories] = useState(normalizeCategoryList(DEFAULT_JEWELRY_METALS));
  const [footerContent, setFooterContent] = useState(DEFAULT_FOOTER_CONTENT);

  const fetchProductCategories = useCallback(() => {
    Promise.all([
      fetch(`${API_BASE}/navbar-dropdown`).then((res) => res.json()).catch(() => ({})),
      fetch(`${API_BASE}/footer-content`).then((res) => res.json()).catch(() => null),
    ]).then(([navbarData, footerData]) => {
      if (Array.isArray(navbarData.jewelryMetals) && navbarData.jewelryMetals.length) {
        setProductCategories(normalizeCategoryList(navbarData.jewelryMetals));
      }

      if (footerData) {
        setFooterContent({
          ...DEFAULT_FOOTER_CONTENT,
          ...footerData,
        });
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    fetchProductCategories();
  }, [fetchProductCategories]);

  useEffect(() => bindStorefrontRefresh(fetchProductCategories, { scopes: ['navigation'] }), [fetchProductCategories]);

  const handleLinkClick = (url = '') => {
    const nextUrl = String(url || '').trim();

    if (!nextUrl) {
      return;
    }

    if (/^https?:\/\//i.test(nextUrl)) {
      window.open(nextUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(nextUrl.startsWith('/') ? nextUrl : `/${nextUrl}`);
  };

  const productCategoryLinks = normalizeLinks(footerContent.productCategoryLinks);
  const fallbackProductCategoryLinks = productCategories.map((category, index) => ({
    label: formatMaterialDisplayLabel(category),
    url: `/store?material=${encodeURIComponent(normalizeMaterialLabel(category))}`,
    order: index + 1,
  }));
  const visibleProductCategoryLinks = productCategoryLinks.length ? productCategoryLinks : fallbackProductCategoryLinks;
  const visibleQuickLinks = normalizeLinks(footerContent.quickLinks || DEFAULT_FOOTER_CONTENT.quickLinks);
  const visibleCustomerServiceLinks = normalizeLinks(footerContent.customerServiceLinks || DEFAULT_FOOTER_CONTENT.customerServiceLinks);
  const visibleSocialLinks = normalizeLinks(footerContent.socialLinks || DEFAULT_FOOTER_CONTENT.socialLinks);

  return (
    <footer className="footer">
      <div className="footer-container">
        {/* Company Info */}
        <div className="footer-column">
          <div className="footer-logo">
            <h3><span className="brand-name-gold">Panchali Jewels</span></h3>
            <p>Your trusted partner in fine jewelry</p>
          </div>
          <p className="company-description">
            {footerContent.description}
          </p>
          <div className="contact-info">
            <div className="contact-item">
              <FaPhone className="contact-icon" />
              <span>{footerContent.phone}</span>
            </div>
            <div className="contact-item">
              <FaEnvelope className="contact-icon" />
              <span>{footerContent.email}</span>
            </div>
            <div className="contact-item">
              <FaMapMarkerAlt className="contact-icon" />
              <span>{footerContent.address}</span>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="footer-column">
          <h4>Quick Links</h4>
          <ul>
            {visibleQuickLinks.map((link) => (
              <li key={`${link.label}-${link.url}`} onClick={() => handleLinkClick(link.url)}>{link.label}</li>
            ))}
          </ul>
        </div>

        {/* Product Categories */}
        <div className="footer-column">
          <h4>Product Categories</h4>
          <ul className="product-categories-grid">
            {visibleProductCategoryLinks.map((link) => (
              <li key={`${link.label}-${link.url}`} onClick={() => handleLinkClick(link.url)}>
                {link.label}
              </li>
            ))}
          </ul>
        </div>

        {/* Customer Service */}
        <div className="footer-column customer-service-column">
          <h4>Customer Service</h4>
          <ul>
            {visibleCustomerServiceLinks.map((link) => (
              <li key={`${link.label}-${link.url}`} onClick={() => handleLinkClick(link.url)}>{link.label}</li>
            ))}
          </ul>
          <div className="social-links">
            <h5>Follow Us</h5>
            <div className="social-icons">
              {visibleSocialLinks.map((link) => {
                const Icon = SOCIAL_ICONS[String(link.label || '').trim().toLowerCase()] || FaLinkedin;

                return (
                  <a href={link.url} target="_blank" rel="noopener noreferrer" aria-label={link.label} key={`${link.label}-${link.url}`}>
                    <Icon />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <section className="footer-review-section" aria-labelledby="footer-review-heading">
        <div className="footer-review-content">
          <div>
            <h4 id="footer-review-heading">Reviews</h4>
            <p>
              Share your experience with a delivered order and help other customers shop with confidence.
            </p>
          </div>
          <button type="button" onClick={() => navigate('/order')}>
            Write a Review
          </button>
        </div>
      </section>

      {/* Footer Bottom */}
      <div className="footer-bottom">
        <div className="footer-bottom-content">
          <div className="footer-bottom-left">
            <p>&copy; 2025 <span className="brand-name-gold">Panchali Jewels</span>. All rights reserved.</p>
          </div>
          <div className="footer-bottom-right">
            <Link to="/terms">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/terms">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
